import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatCompactCurrency, formatCurrency, formatNumber } from "../utils/formatters";
import FilterMetricIcon from "./FilterMetricIcon";
import MetricRangeSlider from "./MetricRangeSlider";

const WHAT_IF_ELIGIBLE_SEGMENTS = new Set([
  "Promising",
  "Need Attention",
  "At Risk",
  "Lost",
]);

const WHAT_IF_MIN = 5;
const WHAT_IF_MAX = 100;
const WHAT_IF_STEP = 5;
const WHAT_IF_DEFAULT = 50;
const CUSTOM_MESSAGE_ACTION_TYPE = "__custom_message__";

function normalizeWhatsAppPhone(value) {
  let digits = String(value || "").replace(/\D+/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

function parseWhatsAppPhones(value) {
  return String(value || "")
    .split(/[\n,;]+/)
    .map((item) => normalizeWhatsAppPhone(item))
    .filter(Boolean)
    .filter((item, index, list) => list.indexOf(item) === index);
}

function getMetricRange(rows, key, options = {}) {
  const values = (rows || [])
    .map((row) => Number(row?.[key] || 0))
    .filter((value) => Number.isFinite(value) && (!options.positiveMin || value > 0));

  if (!values.length) {
    return { min: "", max: "" };
  }

  return {
    min: String(Math.floor(Math.min(...values))),
    max: String(Math.ceil(Math.max(...values))),
  };
}

function toTitleCase(value) {
  return String(value || "").toLowerCase().replace(/[A-Za-z]+/g, (word) =>
    word.charAt(0).toUpperCase() + word.slice(1)
  );
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizeMessageTemplate(value) {
  return String(value || "")
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n");
}

function normalizeWhatsAppStatus(value) {
  return String(value || "unknown").trim().toLowerCase().replace(/[\s_-]+/g, "_");
}

function getWhatsAppStatusMeta(value) {
  const normalized = normalizeWhatsAppStatus(value);
  const tokens = normalized.split("_").filter(Boolean);
  if (
    ["rejected", "reject", "failed", "declined"].includes(normalized) ||
    tokens.some((token) => ["rejected", "reject", "failed", "declined"].includes(token))
  ) {
    return { key: "rejected", label: "Rejected" };
  }
  if (
    ["paused", "disabled", "inactive"].includes(normalized) ||
    tokens.some((token) => ["paused", "disabled", "inactive"].includes(token))
  ) {
    return { key: "paused", label: "Paused" };
  }
  if (
    ["approved", "active", "enabled"].includes(normalized) ||
    tokens.some((token) => ["approved", "active", "enabled", "quality"].includes(token)) ||
    normalized.startsWith("active_")
  ) {
    return { key: "approved", label: "Approved" };
  }
  if (
    ["pending", "in_review", "review", "submitted", "requested", "waiting", "processing"].includes(normalized) ||
    tokens.some((token) => ["pending", "review", "submitted", "requested", "waiting", "processing"].includes(token))
  ) {
    return { key: "review", label: "In Review" };
  }
  return { key: "unknown", label: value ? toTitleCase(value) : "Unknown" };
}

function isApprovedWhatsAppTemplate(template) {
  return getWhatsAppStatusMeta(template?.status).key === "approved";
}

function getTemplateParameterNames(template) {
  const candidates = [];
  if (Array.isArray(template?.parameter_names)) {
    template.parameter_names.forEach((name) => {
      if (name) candidates.push(String(name).trim());
    });
  }

  const raw = template?.raw || {};
  const rawParams = raw.customParams || raw.custom_params || raw.parameters || raw.params || [];
  if (Array.isArray(rawParams)) {
    rawParams.forEach((item) => {
      if (!item || typeof item !== "object") return;
      const name = item.name || item.paramName || item.parameterName || item.key || item.field || item.variable;
      if (name) candidates.push(String(name).trim());
    });
  }

  String(template?.body || "").replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (match, name) => {
    candidates.push(String(name).trim());
    return match;
  });

  return [...new Set(candidates.filter(Boolean))];
}

const WHATSAPP_PARAMETER_SOURCES = [
  { key: "customer_name", label: "Customer Name" },
  { key: "first_name", label: "First Name" },
  { key: "phone_number", label: "Phone Number" },
  { key: "segment", label: "Segment" },
  { key: "organization_name", label: "Organization Name" },
  { key: "total_spend", label: "Total Spend" },
  { key: "visit_count", label: "Visit Count" },
  { key: "custom", label: "Custom Text" },
];

function inferWhatsAppParameterSource(name) {
  const normalized = String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "_");
  if (/^(name|customer|customer_name|full_name)$/.test(normalized)) return "customer_name";
  if (/^(first|first_name)$/.test(normalized)) return "first_name";
  if (/^(phone|mobile|phone_number|whatsapp|whatsapp_number)$/.test(normalized)) return "phone_number";
  if (/^(segment|rfm_segment)$/.test(normalized)) return "segment";
  if (/^(org|organization|organization_name|shop|shop_name|brand)$/.test(normalized)) return "organization_name";
  if (/^(spend|total_spend|amount|monetary)$/.test(normalized)) return "total_spend";
  if (/^(visits|visit_count|frequency)$/.test(normalized)) return "visit_count";
  return "";
}

function defaultWhatsAppFallback(source, organizationName) {
  if (source === "organization_name") return organizationName || "Your Shop";
  if (source === "customer_name" || source === "first_name") return "Customer";
  if (source === "segment") return "Customer Segment";
  if (source === "total_spend") return "0";
  if (source === "visit_count") return "0";
  return "";
}

function getCustomerDisplayName(customer) {
  return toTitleCase(customer?.full_name || customer?.first_name || customer?.name || "Customer");
}

function getCustomerPhone(customer) {
  return customer?.phone_number || customer?.mobile || customer?.phone || "";
}

function resolveWhatsAppPreviewValue(source, fallback, customer, context, organizationName) {
  if (source === "customer_name") return getCustomerDisplayName(customer);
  if (source === "first_name") return toTitleCase(String(customer?.first_name || customer?.full_name || "Customer").split(" ")[0]);
  if (source === "phone_number") return getCustomerPhone(customer) || fallback || "";
  if (source === "segment") return context?.segment || fallback || "Customer Segment";
  if (source === "organization_name") return organizationName || fallback || "Your Shop";
  if (source === "total_spend") return formatCurrency(customer?.total_spend || 0);
  if (source === "visit_count") return formatNumber(customer?.visit_count || 0);
  return fallback || "";
}

function applyTemplateParameters(body, valuesByName) {
  return String(body || "").replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (match, name) => valuesByName[name] || match);
}

function normalizeTemplateName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function sanitizeTemplateNameInput(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+/g, "")
    .replace(/_{2,}/g, "_")
    .slice(0, 512);
}

function parseTemplateSampleValues(value) {
  return String(value || "")
    .split(/\r?\n/)
    .map((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      const separator = trimmed.includes("=") ? "=" : trimmed.includes(":") ? ":" : "";
      if (!separator) return { name: String(index + 1), value: trimmed };
      const [name, ...rest] = trimmed.split(separator);
      return { name: name.trim() || String(index + 1), value: rest.join(separator).trim() };
    })
    .filter(Boolean);
}

function getTemplatePlaceholders(body) {
  const matches = String(body || "").match(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g) || [];
  return Array.from(new Set(matches.map((item) => item.replace(/[{}]/g, "").trim()).filter(Boolean)));
}

function validateWhatsAppTemplateDraft({ name, body, sampleValues, footer, category }) {
  const issues = [];
  const rawName = String(name || "").trim();
  const normalizedName = normalizeTemplateName(name);
  const templateBody = String(body || "").trim();
  const templateFooter = String(footer || "").trim();
  const templateCategory = String(category || "").trim().toUpperCase();
  const placeholders = getTemplatePlaceholders(templateBody);
  const samples = parseTemplateSampleValues(sampleValues);
  const sampleMap = new Map(samples.map((item) => [String(item.name || "").trim(), String(item.value || "").trim()]));

  if (!normalizedName) {
    issues.push({ level: "error", title: "Template name is required.", detail: "Use lowercase letters, numbers, and underscores." });
  }
  if (rawName && rawName !== normalizedName) {
    issues.push({ level: "warning", title: "Template name will be converted.", detail: `Provider-safe name: ${normalizedName || "template_name"}.` });
  }
  if (normalizedName && !/^[a-z][a-z0-9_]{2,}$/.test(normalizedName)) {
    issues.push({ level: "error", title: "Template name format is not valid.", detail: "Start with a letter and use at least 3 lowercase characters." });
  }
  if (normalizedName.length > 512) {
    issues.push({ level: "error", title: "Template name is too long.", detail: "Meta template names must be 512 characters or less." });
  }
  if (/__+/.test(normalizedName)) {
    issues.push({ level: "warning", title: "Avoid repeated underscores in the template name.", detail: "Use one underscore between words for easier review." });
  }
  if (!["MARKETING", "UTILITY", "AUTHENTICATION"].includes(templateCategory)) {
    issues.push({ level: "error", title: "Template category is required.", detail: "Choose Marketing, Utility, or Authentication." });
  }
  if (!templateBody) {
    issues.push({ level: "error", title: "Template body is required.", detail: "Add the actual WhatsApp message copy." });
  }
  if (templateBody.length > 1024) {
    issues.push({ level: "error", title: "Template body is too long.", detail: "Keep the message body under 1,024 characters for standard review." });
  }
  if (templateFooter.length > 60) {
    issues.push({ level: "error", title: "Footer is too long.", detail: "Keep the footer under 60 characters." });
  }
  if (/\{\{[^}]*$|^[^{]*\}\}/.test(templateBody) || /\{[^{]|\}[^}]/.test(templateBody.replace(/\{\{\s*[A-Za-z0-9_]+\s*\}\}/g, ""))) {
    issues.push({ level: "error", title: "Placeholder braces are not valid.", detail: "Use variables exactly like {{1}} or {{customer_name}}." });
  }
  if (/^\s*\{\{\s*[A-Za-z0-9_]+\s*\}\}/.test(templateBody)) {
    issues.push({ level: "error", title: "Do not start with a variable.", detail: "Add a greeting or clear text before the first placeholder." });
  }
  if (/\{\{\s*[A-Za-z0-9_]+\s*\}\}\s*[.!?]?\s*$/.test(templateBody)) {
    issues.push({ level: "error", title: "Do not end with a variable.", detail: "Add meaningful text after the final placeholder." });
  }
  if (/\}\}\s*\{\{/.test(templateBody)) {
    issues.push({ level: "error", title: "Variables cannot be adjacent.", detail: "Add words between placeholders." });
  }

  const words = templateBody.replace(/\{\{\s*[A-Za-z0-9_]+\s*\}\}/g, " ").trim().split(/\s+/).filter(Boolean);
  if (placeholders.length >= 3 && words.length < placeholders.length * 2) {
    issues.push({ level: "error", title: "Template has too many variables for the amount of text.", detail: "Add stable message copy so the template is not mostly placeholders." });
  }

  const numericPlaceholders = placeholders.filter((name) => /^\d+$/.test(name)).map((name) => Number(name)).sort((a, b) => a - b);
  numericPlaceholders.forEach((value, index) => {
    if (value !== index + 1) {
      issues.push({ level: "error", title: "Numeric variables must be sequential.", detail: "Use {{1}}, {{2}}, {{3}} without skipping numbers." });
    }
  });

  const seenSampleNames = new Set();
  samples.forEach((item) => {
    const sampleName = String(item.name || "").trim();
    if (!sampleName) return;
    if (seenSampleNames.has(sampleName)) {
      issues.push({ level: "error", title: `Duplicate sample value for {{${sampleName}}}.`, detail: "Each variable should have one sample line." });
    }
    seenSampleNames.add(sampleName);
  });

  placeholders.forEach((name) => {
    const sample = sampleMap.get(name);
    if (!sample) {
      issues.push({ level: "error", title: `Sample value missing for {{${name}}}.`, detail: "Add one line like customer_name=Priya." });
      return;
    }
    if (/^(undefined|null|none|test|sample|n\/a)$/i.test(sample)) {
      issues.push({ level: "error", title: `Sample value for {{${name}}} is not meaningful.`, detail: "Use a realistic value a customer would see." });
    }
  });

  samples.forEach((item) => {
    const sampleName = String(item.name || "").trim();
    if (sampleName && !placeholders.includes(sampleName)) {
      issues.push({ level: "warning", title: `Sample {{${sampleName}}} is not used in the body.`, detail: "Remove unused samples or add the matching placeholder." });
    }
  });

  return {
    isValid: !issues.some((issue) => issue.level === "error"),
    issues,
    placeholders,
    samples,
    normalizedName,
  };
}

export default function SegmentActionModal({
  context,
  organizationName = "Your Shop",
  terminology = {},
  templates = {},
  customers = [],
  whatsappConnection = null,
  whatsappTemplates = [],
  whatsappTemplateLoadError = "",
  whatsappAnalytics = null,
  suggestiveActionsConfig = { custom_actions: [], hidden_action_keys: [], deleted_action_keys: [], usage_count: {} },
  onClose,
  onRunAction,
  onSendWhatsAppCampaign,
  onCreateWhatsAppTemplateRequest,
  onResetTemplate,
  onUpdateSuggestiveAction,
  onToggleSuggestiveAction,
  onDeleteSuggestiveAction,
}) {
  const [recoveryPct, setRecoveryPct] = useState(WHAT_IF_DEFAULT);
  const [selectedActionType, setSelectedActionType] = useState(null);
  const [draftMessage, setDraftMessage] = useState("");
  const [sendingActionType, setSendingActionType] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isPoolDragOver, setIsPoolDragOver] = useState(false);
  const [isPoolOpen, setIsPoolOpen] = useState(false);
  const [editingActionType, setEditingActionType] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [isUpdatingAction, setIsUpdatingAction] = useState(false);
  const [customMessageText, setCustomMessageText] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [isRecipientPickerOpen, setIsRecipientPickerOpen] = useState(false);
  const [recipientPickerAction, setRecipientPickerAction] = useState(null);
  const [isCustomRecipientFlow, setIsCustomRecipientFlow] = useState(false);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);
  const [recipientSearchText, setRecipientSearchText] = useState("");
  const [recipientMinVisits, setRecipientMinVisits] = useState("");
  const [recipientMaxVisits, setRecipientMaxVisits] = useState("");
  const [recipientMinMonetary, setRecipientMinMonetary] = useState("");
  const [recipientMaxMonetary, setRecipientMaxMonetary] = useState("");
  const [workspaceTab, setWorkspaceTab] = useState("campaign");
  const [campaignWorkspace, setCampaignWorkspace] = useState("library");
  const [testWorkspace, setTestWorkspace] = useState("numbers");
  const [templateLibraryFilter, setTemplateLibraryFilter] = useState("approved");
  const [templateLibraryQuery, setTemplateLibraryQuery] = useState("");
  const [isTemplateSearchOpen, setIsTemplateSearchOpen] = useState(false);
  const [libraryTemplateName, setLibraryTemplateName] = useState("");
  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [isTemplatePickerOpen, setIsTemplatePickerOpen] = useState(false);
  const [templatePickerQuery, setTemplatePickerQuery] = useState("");
  const [templateParameterMappings, setTemplateParameterMappings] = useState({});
  const [isWhatsAppReviewOpen, setIsWhatsAppReviewOpen] = useState(false);
  const [isWhatsAppRecipientPickerOpen, setIsWhatsAppRecipientPickerOpen] = useState(false);
  const [whatsappSelectedRecipientIds, setWhatsappSelectedRecipientIds] = useState([]);
  const [whatsappRecipientSearchText, setWhatsappRecipientSearchText] = useState("");
  const [isWhatsAppSending, setIsWhatsAppSending] = useState(false);
  const [isWhatsAppTestSending, setIsWhatsAppTestSending] = useState(false);
  const [whatsappSendError, setWhatsappSendError] = useState("");
  const [testPhonesText, setTestPhonesText] = useState("");
  const [testSendResult, setTestSendResult] = useState(null);
  const [isTemplateRequestOpen, setIsTemplateRequestOpen] = useState(false);
  const [templateRequestName, setTemplateRequestName] = useState("");
  const [templateRequestCategory, setTemplateRequestCategory] = useState("MARKETING");
  const [templateRequestBody, setTemplateRequestBody] = useState("");
  const [templateRequestFooter, setTemplateRequestFooter] = useState("");
  const [templateRequestSamples, setTemplateRequestSamples] = useState("");
  const [isSubmittingTemplateRequest, setIsSubmittingTemplateRequest] = useState(false);
  const [hasTemplateSubmitAttempted, setHasTemplateSubmitAttempted] = useState(false);
  const actionCardRefs = useRef(new Map());
  const templateSearchInputRef = useRef(null);
  const activityCountLabel = terminology.activity_count_label || terminology.activity_plural_label || "Activities";
  const spendLabel = terminology.spend_label || "Monetary";
  const totalSpendLabel = terminology.total_spend_label || "Total Spend";
  const customerPlural = terminology.customer_plural_label || "customers";

  const applyOrganizationName = useCallback((value) => {
    const rawOrganizationName = String(organizationName || "").trim();
    const normalizedOrganizationName = String(organizationName || "Your Shop")
      .replace(/\bprivate\s+limited\b/gi, "")
      .replace(/\bpvt\.?\s*ltd\.?\b/gi, "")
      .replace(/\s{2,}/g, " ")
      .trim() || "Your Shop";

    let text = normalizeMessageTemplate(value);
    if (!text) {
      return text;
    }

    text = text
      .replace(/\[Optical Shop Name\]/gi, normalizedOrganizationName)
      .replace(/\[Logistics Company\]/gi, normalizedOrganizationName)
      .replace(/\[Shop Name\]/gi, normalizedOrganizationName)
      .replace(/\[Your Shop\]/gi, normalizedOrganizationName)
      .replace(/\boptical shop name\b/gi, normalizedOrganizationName)
      .replace(/\byour shop\b/gi, normalizedOrganizationName)
      .replace(/\byour shope\b/gi, normalizedOrganizationName);

    if (rawOrganizationName && rawOrganizationName.toLowerCase() !== normalizedOrganizationName.toLowerCase()) {
      text = text.replace(new RegExp(escapeRegExp(rawOrganizationName), "gi"), normalizedOrganizationName);
    }

    const filtered = text
      .split("\n")
      .filter((line) => !/^\s*reply\b/i.test(line.trim()))
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ \t]{2,}/g, " ")
      .trim();

    return filtered;
  }, [organizationName]);

  useEffect(() => {
    if (context) {
      setRecoveryPct(WHAT_IF_DEFAULT);
      setSelectedActionType(null);
      setDraftMessage("");
      setIsPoolOpen(false);
      setConfirmAction(null);
      setIsRecipientPickerOpen(false);
      setRecipientPickerAction(null);
      setIsCustomRecipientFlow(false);
      setSelectedRecipientIds([]);
      setRecipientSearchText("");
      setRecipientMinVisits("");
      setRecipientMaxVisits("");
      setRecipientMinMonetary("");
      setRecipientMaxMonetary("");
      setWorkspaceTab("campaign");
      setCampaignWorkspace("library");
      setTestWorkspace("numbers");
      setTemplateLibraryFilter("approved");
      setTemplateLibraryQuery("");
      setIsTemplateSearchOpen(false);
      setLibraryTemplateName("");
      setSelectedTemplateName("");
      setIsTemplatePickerOpen(false);
      setTemplatePickerQuery("");
      setTemplateParameterMappings({});
      setIsWhatsAppReviewOpen(false);
      setIsWhatsAppRecipientPickerOpen(false);
      setWhatsappSelectedRecipientIds([]);
      setWhatsappRecipientSearchText("");
      setWhatsappSendError("");
      setTestPhonesText("");
      setTestSendResult(null);
      setIsTemplateRequestOpen(false);
      setTemplateRequestName("");
      setTemplateRequestCategory("MARKETING");
      setTemplateRequestBody("");
      setTemplateRequestFooter("");
      setTemplateRequestSamples("");
      setHasTemplateSubmitAttempted(false);
    }
  }, [context]);

  useEffect(() => {
    function onEsc(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    if (context) {
      window.addEventListener("keydown", onEsc);
    }

    return () => window.removeEventListener("keydown", onEsc);
  }, [context, onClose]);

  useEffect(() => {
    if (!context || typeof document === "undefined") {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [context]);

  const whatIf = useMemo(() => {
    if (!context) {
      return { recoveredCustomers: 0, potentialRevenue: 0 };
    }

    const recoveredCustomers = Math.round((context.customerCount || 0) * (recoveryPct / 100));
    const potentialRevenue = (context.revenue || 0) * (recoveryPct / 100);

    return { recoveredCustomers, potentialRevenue };
  }, [context, recoveryPct]);

  const actions = useMemo(() => {
    if (!context?.segment) {
      return [];
    }

    const dbActions = (suggestiveActionsConfig?.custom_actions || [])
      .filter((item) => item.segment === context.segment)
      .map((item) => ({
        type: item.action_type,
        title: item.title,
        note: item.note || (item.is_custom ? "Custom suggestive action" : ""),
        messageTemplate: item.message_template || item.messageTemplate || `${item.title}\n\n${item.note || ""}`,
        isCustom: !!item.is_custom,
      }));

    return dbActions;
  }, [context?.segment, suggestiveActionsConfig?.custom_actions]);

  const hiddenActionKeys = useMemo(
    () => new Set(suggestiveActionsConfig?.hidden_action_keys || []),
    [suggestiveActionsConfig?.hidden_action_keys]
  );

  const deletedActionKeys = useMemo(
    () => new Set(suggestiveActionsConfig?.deleted_action_keys || []),
    [suggestiveActionsConfig?.deleted_action_keys]
  );

  const usageCount = suggestiveActionsConfig?.usage_count || {};

  const availableActions = useMemo(
    () =>
      actions.filter(
        (action) => !deletedActionKeys.has(`${context?.segment}::${action.type}`)
      ),
    [actions, context?.segment, deletedActionKeys]
  );

  const segmentActions = useMemo(
    () =>
      availableActions.filter(
        (action) => !hiddenActionKeys.has(`${context?.segment}::${action.type}`)
      ),
    [availableActions, context?.segment, hiddenActionKeys]
  );

  const poolActions = useMemo(
    () => availableActions,
    [availableActions]
  );

  const displayedSegmentActions = useMemo(
    () => [
      ...segmentActions,
      {
        type: CUSTOM_MESSAGE_ACTION_TYPE,
        title: "Send a Custom Message",
        note: "Write your own message and send directly to this segment.",
        isCustomMessage: true,
      },
    ],
    [segmentActions]
  );

  const selectedAction =
    availableActions.find((item) => item.type === selectedActionType) || null;
  const isWhatIfEligible = WHAT_IF_ELIGIBLE_SEGMENTS.has(context?.segment || "");
  const segmentCustomers = useMemo(
    () => (customers || []).filter((item) => item.segment === context?.segment),
    [context?.segment, customers]
  );
  const segmentContribution = useMemo(() => {
    const segmentRevenue = Number(context?.revenue || 0);
    const portfolioRevenue = (customers || []).reduce((total, customer) => total + Number(customer.total_spend || customer.monetary || customer.revenue || 0), 0);
    const revenueShare = portfolioRevenue > 0 ? (segmentRevenue / portfolioRevenue) * 100 : 0;
    const customerCount = Number(context?.customerCount || segmentCustomers.length || 0);
    const averageSpend = customerCount > 0 ? segmentRevenue / customerCount : 0;

    return {
      averageSpend,
      revenueShare,
      segmentRevenue,
    };
  }, [context?.customerCount, context?.revenue, customers, segmentCustomers.length]);
  const hasWhatsAppStudio = Boolean(onSendWhatsAppCampaign || onCreateWhatsAppTemplateRequest);
  const approvedWhatsAppTemplates = useMemo(
    () => (whatsappTemplates || []).filter((template) => isApprovedWhatsAppTemplate(template)),
    [whatsappTemplates]
  );
  const selectedWhatsAppTemplate = useMemo(
    () => approvedWhatsAppTemplates.find((template) => template.name === selectedTemplateName) || null,
    [approvedWhatsAppTemplates, selectedTemplateName]
  );
  const selectedTemplateParameterNames = useMemo(
    () => getTemplateParameterNames(selectedWhatsAppTemplate),
    [selectedWhatsAppTemplate]
  );
  const whatsAppTemplateStats = useMemo(() => {
    const stats = { total: 0, approved: 0, review: 0, rejected: 0, paused: 0, unknown: 0 };
    (whatsappTemplates || []).forEach((template) => {
      stats.total += 1;
      const key = getWhatsAppStatusMeta(template?.status).key;
      stats[key] = (stats[key] || 0) + 1;
    });
    return stats;
  }, [whatsappTemplates]);
  const templateFilterOptions = useMemo(() => ([
    { key: "approved", label: "Approved", value: whatsAppTemplateStats.approved, className: "approved" },
    { key: "review", label: "In Review", value: whatsAppTemplateStats.review, className: "review" },
    { key: "rejected", label: "Rejected", value: whatsAppTemplateStats.rejected, className: "rejected" },
  ]), [whatsAppTemplateStats]);
  const filteredLibraryTemplates = useMemo(() => {
    const query = templateLibraryQuery.trim().toLowerCase();
    return (whatsappTemplates || [])
      .filter((template) => getWhatsAppStatusMeta(template?.status).key === templateLibraryFilter)
      .filter((template) => {
        if (!query) return true;
        return [template.name, template.display_name, template.category, template.language, template.body, template.status]
          .some((value) => String(value || "").toLowerCase().includes(query));
      });
  }, [templateLibraryFilter, templateLibraryQuery, whatsappTemplates]);
  const previewWhatsAppTemplate = useMemo(
    () =>
      (whatsappTemplates || []).find((template) => template.name === libraryTemplateName) ||
      selectedWhatsAppTemplate ||
      filteredLibraryTemplates[0] ||
      null,
    [filteredLibraryTemplates, libraryTemplateName, selectedWhatsAppTemplate, whatsappTemplates]
  );
  const previewWhatsAppStatus = getWhatsAppStatusMeta(previewWhatsAppTemplate?.status);
  const templateLibraryGroups = useMemo(
    () =>
      templateFilterOptions
        .map((option) => ({
          ...option,
          templates: filteredLibraryTemplates.filter((template) => getWhatsAppStatusMeta(template?.status).key === option.key),
        }))
        .filter((group) => group.templates.length || ["approved", "review", "rejected"].includes(group.key)),
    [filteredLibraryTemplates, templateFilterOptions]
  );
  const visibleTemplateLibraryGroups = useMemo(
    () => templateLibraryGroups.filter((group) => group.key === templateLibraryFilter),
    [templateLibraryFilter, templateLibraryGroups]
  );
  const filteredApprovedWhatsAppTemplates = useMemo(() => {
    const query = templatePickerQuery.trim().toLowerCase();
    if (!query) return approvedWhatsAppTemplates;
    return approvedWhatsAppTemplates.filter((template) =>
      [template.name, template.display_name, template.category, template.language, template.body]
        .some((value) => String(value || "").toLowerCase().includes(query))
    );
  }, [approvedWhatsAppTemplates, templatePickerQuery]);
  const normalizedTestPhones = useMemo(() => parseWhatsAppPhones(testPhonesText), [testPhonesText]);
  const isTestingMode = workspaceTab === "test";
  const testPreviewRecipient = useMemo(() => {
    const phoneNumber = normalizedTestPhones[0] || "";
    if (!phoneNumber) return null;
    return {
      customer_id: "",
      phone_number: phoneNumber,
      full_name: "Test Customer",
      first_name: "Test",
      segment: context?.segment || "",
      total_spend: 0,
      visit_count: 0,
    };
  }, [context?.segment, normalizedTestPhones]);
  const previewRecipient = isTestingMode ? testPreviewRecipient : segmentCustomers[0] || null;
  const previewParameterValues = useMemo(() => {
    const values = {};
    selectedTemplateParameterNames.forEach((name) => {
      const mapping = templateParameterMappings[name] || {};
      values[name] = resolveWhatsAppPreviewValue(mapping.source, mapping.fallback, previewRecipient, context, organizationName);
    });
    return values;
  }, [context, organizationName, previewRecipient, selectedTemplateParameterNames, templateParameterMappings]);
  const previewMessageBody = useMemo(
    () => applyTemplateParameters(selectedWhatsAppTemplate?.body || "", previewParameterValues),
    [previewParameterValues, selectedWhatsAppTemplate?.body]
  );
  const missingWhatsAppParameterMappings = useMemo(
    () => selectedTemplateParameterNames.filter((name) => !(templateParameterMappings[name]?.source)),
    [selectedTemplateParameterNames, templateParameterMappings]
  );
  const filteredWhatsAppRecipients = useMemo(() => {
    const query = whatsappRecipientSearchText.trim().toLowerCase();
    if (!query) return segmentCustomers;
    return segmentCustomers.filter((customer) =>
      [customer.full_name, customer.first_name, customer.phone_number]
        .some((value) => String(value || "").toLowerCase().includes(query))
    );
  }, [segmentCustomers, whatsappRecipientSearchText]);
  const whatsappSelectedCount = whatsappSelectedRecipientIds.length || segmentCustomers.length;
  const whatsappCostRate = selectedWhatsAppTemplate?.category === "UTILITY" || selectedWhatsAppTemplate?.category === "AUTHENTICATION" ? 0.115 : 0.7846;
  const whatsappEstimatedCost = whatsappSelectedCount * whatsappCostRate;
  const activeWorkspace = isTestingMode ? testWorkspace : campaignWorkspace;
  const activeRecipientCount = isTestingMode ? normalizedTestPhones.length : whatsappSelectedCount;
  const activeEstimatedCost = activeRecipientCount * whatsappCostRate;
  const testSendFailureMessages = useMemo(() => {
    const failures = Array.isArray(testSendResult?.failures) ? testSendResult.failures : [];
    return failures
      .map((failure) => {
        const phoneNumber = String(failure?.phone_number || "").trim();
        const reason = String(failure?.reason || "").trim();
        return [phoneNumber, reason].filter(Boolean).join(": ");
      })
      .filter(Boolean);
  }, [testSendResult]);
  const templateRequestValidation = useMemo(
    () => validateWhatsAppTemplateDraft({
      name: templateRequestName,
      body: templateRequestBody,
      sampleValues: templateRequestSamples,
      footer: templateRequestFooter,
      category: templateRequestCategory,
    }),
    [templateRequestBody, templateRequestCategory, templateRequestFooter, templateRequestName, templateRequestSamples]
  );
  const templateRequestIssuesByField = useMemo(() => {
    const fields = { name: [], category: [], body: [], footer: [], samples: [] };
    templateRequestValidation.issues.forEach((issue) => {
      const text = `${issue.title} ${issue.detail}`.toLowerCase();
      if (text.includes("template name")) {
        fields.name.push(issue);
      } else if (text.includes("category")) {
        fields.category.push(issue);
      } else if (text.includes("footer")) {
        fields.footer.push(issue);
      } else if (text.includes("sample") || text.includes("duplicate") || text.includes("unused")) {
        fields.samples.push(issue);
      } else {
        fields.body.push(issue);
      }
    });
    return fields;
  }, [templateRequestValidation.issues]);
  const recipientVisitRange = useMemo(() => getMetricRange(segmentCustomers, "visit_count"), [segmentCustomers]);
  const recipientMonetaryRange = useMemo(() => getMetricRange(segmentCustomers, "total_spend"), [segmentCustomers]);

  useEffect(() => {
    if (!isRecipientPickerOpen) {
      return;
    }

    setRecipientMinVisits(recipientVisitRange.min);
    setRecipientMaxVisits(recipientVisitRange.max);
    setRecipientMinMonetary(recipientMonetaryRange.min);
    setRecipientMaxMonetary(recipientMonetaryRange.max);
  }, [
    isRecipientPickerOpen,
    recipientVisitRange.min,
    recipientVisitRange.max,
    recipientMonetaryRange.min,
    recipientMonetaryRange.max,
  ]);

  const filteredSegmentCustomers = useMemo(() => {
    const query = recipientSearchText.trim().toLowerCase();
    const minV = recipientMinVisits ? parseInt(recipientMinVisits, 10) : 0;
    const maxV = recipientMaxVisits ? parseInt(recipientMaxVisits, 10) : Infinity;
    const minM = recipientMinMonetary ? parseFloat(recipientMinMonetary) : 0;
    const maxM = recipientMaxMonetary ? parseFloat(recipientMaxMonetary) : Infinity;

    return segmentCustomers.filter((item) => {
      const matchesSearch =
        !query ||
        String(item.first_name || "").toLowerCase().includes(query) ||
        String(item.phone_number || "").toLowerCase().includes(query);
      const matchesVisits = item.visit_count >= minV && item.visit_count <= maxV;
      const matchesMonetary = item.total_spend >= minM && item.total_spend <= maxM;

      return matchesSearch && matchesVisits && matchesMonetary;
    });
  }, [recipientSearchText, segmentCustomers, recipientMinVisits, recipientMaxVisits, recipientMinMonetary, recipientMaxMonetary]);

  useEffect(() => {
    if (!approvedWhatsAppTemplates.length) {
      setSelectedTemplateName("");
      return;
    }
    setSelectedTemplateName((current) => {
      if (current && approvedWhatsAppTemplates.some((template) => template.name === current)) {
        return current;
      }
      return approvedWhatsAppTemplates[0]?.name || "";
    });
  }, [approvedWhatsAppTemplates]);

  useEffect(() => {
    setTemplateParameterMappings((current) => {
      const next = {};
      selectedTemplateParameterNames.forEach((name) => {
        const existing = current[name] || {};
        const inferred = inferWhatsAppParameterSource(name);
        const source = existing.source || inferred;
        next[name] = {
          source,
          fallback: existing.fallback ?? defaultWhatsAppFallback(source, organizationName),
        };
      });
      return next;
    });
  }, [organizationName, selectedTemplateParameterNames]);

  useEffect(() => {
    if (!selectedAction || !context) {
      setDraftMessage("");
      return;
    }

    const key = `${context.segment}::${selectedAction.type}`;
    setDraftMessage(
      applyOrganizationName(
        templates[key] || selectedAction.messageTemplate || `${selectedAction.title}\n\n${selectedAction.note}`
      )
    );
  }, [applyOrganizationName, context, organizationName, selectedAction, templates]);

  useEffect(() => {
    if (!selectedActionType) {
      return;
    }
    const node = actionCardRefs.current.get(selectedActionType);
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "auto", block: "nearest" });
    }
  }, [selectedActionType]);

  if (!context) {
    return null;
  }

  function onSelectAction(action) {
    if (selectedActionType === action.type) {
      setSelectedActionType(null);
      setDraftMessage("");
      return;
    }
    setSelectedActionType(action.type);
  }

  function isInSegment(action) {
    if (!context || !action) {
      return false;
    }
    return !hiddenActionKeys.has(`${context.segment}::${action.type}`);
  }

  function isActionVisible(action) {
    if (!context || !action) {
      return true;
    }
    const key = `${context.segment}::${action.type}`;
    return !hiddenActionKeys.has(key) && !deletedActionKeys.has(key);
  }

  function isNewAction(action) {
    if (!context || !action?.isCustom) {
      return false;
    }
    const key = `${context.segment}::${action.type}`;
    return Number(usageCount[key] || 0) < 2;
  }

  function onSaveAndSendClick(action, event) {
    event?.stopPropagation();
    if (!action || !context) {
      return;
    }

    setConfirmAction(action);
  }

  async function onConfirmSaveAndSend(event) {
    event?.stopPropagation();
    const action = confirmAction;
    if (!action || !context || !onRunAction) {
      return;
    }

    setSendingActionType(`${action.type}::save-send`);
    try {
      await onRunAction(action, draftMessage);
      setConfirmAction(null);
    } finally {
      setSendingActionType(null);
    }
  }

  function onOpenRecipientPicker(action, event) {
    event?.stopPropagation();
    if (!action || !context) {
      return;
    }

    setRecipientPickerAction(action);
    setIsCustomRecipientFlow(false);
    setSelectedRecipientIds(segmentCustomers.map((item) => item.customer_id));
    setRecipientSearchText("");
    setIsRecipientPickerOpen(true);
  }

  function onOpenCustomRecipientPicker(event) {
    event?.stopPropagation();
    if (!context) {
      return;
    }

    const baseAction = segmentActions[0] || availableActions[0];
    if (!baseAction || !customMessageText.trim()) {
      return;
    }

    setRecipientPickerAction(baseAction);
    setIsCustomRecipientFlow(true);
    setSelectedRecipientIds(segmentCustomers.map((item) => item.customer_id));
    setRecipientSearchText("");
    setIsRecipientPickerOpen(true);
  }

  function onToggleRecipient(customerId) {
    setSelectedRecipientIds((current) =>
      current.includes(customerId)
        ? current.filter((item) => item !== customerId)
        : [...current, customerId]
    );
  }

  function handleRecipientVisitsInput(value) {
    const numValue = value.replace(/[^0-9]/g, "");
    setRecipientMinVisits(numValue);
  }

  function handleRecipientVisitsMaxInput(value) {
    const numValue = value.replace(/[^0-9]/g, "");
    setRecipientMaxVisits(numValue);
  }

  function handleRecipientMonetaryInput(value) {
    const numValue = value.replace(/[^0-9.]/g, "");
    setRecipientMinMonetary(numValue);
  }

  function handleRecipientMonetaryMaxInput(value) {
    const numValue = value.replace(/[^0-9.]/g, "");
    setRecipientMaxMonetary(numValue);
  }

  function resetRecipientFilters() {
    setRecipientMinVisits(recipientVisitRange.min);
    setRecipientMaxVisits(recipientVisitRange.max);
    setRecipientMinMonetary(recipientMonetaryRange.min);
    setRecipientMaxMonetary(recipientMonetaryRange.max);
  }

  const recipientHasActiveFilters =
    recipientMinVisits !== recipientVisitRange.min ||
    recipientMaxVisits !== recipientVisitRange.max ||
    recipientMinMonetary !== recipientMonetaryRange.min ||
    recipientMaxMonetary !== recipientMonetaryRange.max;

  async function onConfirmTargetedSend(event) {
    event?.stopPropagation();
    if (!context || !recipientPickerAction || !selectedRecipientIds.length) {
      return;
    }

    const actionMessage = isCustomRecipientFlow
      ? customMessageText.trim()
      : selectedAction?.type === recipientPickerAction.type
      ? draftMessage
      : resolveMessageForAction(recipientPickerAction);

    if (!actionMessage) {
      return;
    }

    setSendingActionType(`${recipientPickerAction.type}::targeted-send`);
    try {
      await onRunAction?.(recipientPickerAction, actionMessage, selectedRecipientIds);
      setIsRecipientPickerOpen(false);
      setRecipientPickerAction(null);
      setIsCustomRecipientFlow(false);
      setSelectedRecipientIds([]);
      if (isCustomRecipientFlow) {
        setCustomMessageText("");
      }
    } finally {
      setSendingActionType(null);
    }
  }
  function onActionDragStart(actionType, source) {
    setDragItem({ actionType, source });
  }

  async function onDropToSegment(event) {
    event.preventDefault();
    setIsDragOver(false);
    if (!context || !dragItem?.actionType) {
      return;
    }
    await onToggleSuggestiveAction?.(context.segment, dragItem.actionType, false);
    setDragItem(null);
  }

  function onDragOverSegment(event) {
    event.preventDefault();
    setIsDragOver(true);
  }

  async function onDropToPool(event) {
    event.preventDefault();
    setIsPoolDragOver(false);
    if (!context || !dragItem?.actionType) {
      return;
    }
    await onToggleSuggestiveAction?.(context.segment, dragItem.actionType, true);
    setDragItem(null);
  }

  function onDragOverPool(event) {
    event.preventDefault();
    setIsPoolDragOver(true);
  }

  function resolveMessageForAction(action) {
    if (!action || !context) {
      return "";
    }
    if (selectedAction?.type === action.type) {
      return normalizeMessageTemplate(draftMessage);
    }
    const key = `${context.segment}::${action.type}`;
    return applyOrganizationName(
      templates[key] || action.messageTemplate || `${action.title}\n\n${action.note}`
    );
  }

  async function onResetClick(action, event) {
    event?.stopPropagation();
    if (!action || !context) {
      return;
    }
    await onResetTemplate?.(context.segment, action.type);
    setDraftMessage(applyOrganizationName(action.messageTemplate || `${action.title}\n\n${action.note}`));
  }

  async function onSendCustomMessage(event) {
    event?.stopPropagation();
    const normalizedMessage = customMessageText.trim();
    if (!normalizedMessage || !onRunAction) {
      return;
    }

    const baseAction = segmentActions[0] || availableActions[0];
    if (!baseAction) {
      return;
    }

    setSendingActionType("custom");
    try {
      await onRunAction(baseAction, normalizedMessage);
      setCustomMessageText("");
    } finally {
      setSendingActionType(null);
    }
  }

  function onBeginEditAction(action, event) {
    event?.stopPropagation();
    setEditingActionType(action.type);
    setEditTitle(action.title || "");
    setEditNote(action.note || "");
    setEditMessage(resolveMessageForAction(action));
  }

  async function onSaveEditAction(event) {
    event?.stopPropagation();
    if (!context || !editingActionType || !editTitle.trim()) {
      return;
    }
    setIsUpdatingAction(true);
    try {
      await onUpdateSuggestiveAction?.(context.segment, editingActionType, {
        title: editTitle.trim(),
        note: editNote.trim(),
        messageTemplate: editMessage.trim(),
      });
      setEditingActionType(null);
      setEditTitle("");
      setEditNote("");
      setEditMessage("");
    } finally {
      setIsUpdatingAction(false);
    }
  }

  function onCancelEditAction(event) {
    event?.stopPropagation();
    setEditingActionType(null);
    setEditTitle("");
    setEditNote("");
    setEditMessage("");
  }

  function onOpenWhatsAppRecipientPicker(event) {
    event?.stopPropagation();
    setWhatsappSelectedRecipientIds(segmentCustomers.map((item) => item.customer_id));
    setWhatsappRecipientSearchText("");
    setIsWhatsAppRecipientPickerOpen(true);
  }

  function onToggleTestingMode() {
    setWhatsappSendError("");
    setTestSendResult(null);
    setWorkspaceTab((current) => {
      const next = current === "test" ? "campaign" : "test";
      if (next === "test") {
        setTestWorkspace("numbers");
      } else {
        setCampaignWorkspace("library");
      }
      return next;
    });
  }

  function setActiveWorkspace(nextWorkspace) {
    if (workspaceTab === "test") {
      setTestWorkspace(nextWorkspace);
      return;
    }
    setCampaignWorkspace(nextWorkspace);
  }

  function onToggleWhatsAppRecipient(customerId) {
    setWhatsappSelectedRecipientIds((current) =>
      current.includes(customerId)
        ? current.filter((item) => item !== customerId)
        : [...current, customerId]
    );
  }

  function onOpenWhatsAppReview(event, recipientIds = null) {
    event?.stopPropagation();
    setWhatsappSendError("");
    if (!selectedWhatsAppTemplate || !isApprovedWhatsAppTemplate(selectedWhatsAppTemplate)) {
      setWhatsappSendError("Select an approved Meta template first.");
      return;
    }
    if (!whatsappConnection?.connected) {
      setWhatsappSendError("Connect Meta WhatsApp before sending a campaign.");
      return;
    }
    if (missingWhatsAppParameterMappings.length) {
      setWhatsappSendError(`Map ${missingWhatsAppParameterMappings.join(", ")} before sending.`);
      return;
    }
    if (Array.isArray(recipientIds)) {
      setWhatsappSelectedRecipientIds(recipientIds);
    }
    setIsWhatsAppReviewOpen(true);
  }

  async function onConfirmWhatsAppCampaign(event) {
    event?.stopPropagation();
    if (!selectedWhatsAppTemplate || !context || !onSendWhatsAppCampaign) {
      return;
    }
    const selectedIds =
      whatsappSelectedRecipientIds.length && whatsappSelectedRecipientIds.length !== segmentCustomers.length
        ? whatsappSelectedRecipientIds
        : null;

    setIsWhatsAppSending(true);
    setWhatsappSendError("");
    try {
      await onSendWhatsAppCampaign({
        segment: context.segment,
        action_type: "whatsapp_template",
        title: `${context.segment} WhatsApp campaign`,
        note: context.segment_description || context.recommended_action || "",
        template_name: selectedWhatsAppTemplate.name,
        language_code: selectedWhatsAppTemplate.language || "en_US",
        recipient_customer_ids: selectedIds,
        parameter_mappings: selectedTemplateParameterNames.map((name) => ({
          name,
          source: templateParameterMappings[name]?.source || "custom",
          fallback: templateParameterMappings[name]?.fallback || "",
        })),
      });
      setIsWhatsAppReviewOpen(false);
      setIsWhatsAppRecipientPickerOpen(false);
      setWhatsappSelectedRecipientIds([]);
    } catch (error) {
      setWhatsappSendError(error?.response?.data?.detail || error?.message || "Failed to send WhatsApp campaign.");
    } finally {
      setIsWhatsAppSending(false);
    }
  }

  async function onConfirmWhatsAppTest(event) {
    event?.stopPropagation();
    setWhatsappSendError("");
    setTestSendResult(null);
    if (!selectedWhatsAppTemplate || !context || !onSendWhatsAppCampaign || !normalizedTestPhones.length) {
      return;
    }
    if (!whatsappConnection?.connected) {
      setWhatsappSendError("Connect Meta WhatsApp before sending a test.");
      return;
    }
    if (missingWhatsAppParameterMappings.length) {
      setWhatsappSendError(`Map ${missingWhatsAppParameterMappings.join(", ")} before sending.`);
      return;
    }

    setIsWhatsAppTestSending(true);
    try {
      const result = await onSendWhatsAppCampaign({
        segment: context.segment,
        action_type: "whatsapp_template_test",
        title: `${context.segment} WhatsApp test`,
        note: `Test send to ${normalizedTestPhones.length} number${normalizedTestPhones.length === 1 ? "" : "s"}.`,
        template_name: selectedWhatsAppTemplate.name,
        language_code: selectedWhatsAppTemplate.language || "en_US",
        test_phone_numbers: normalizedTestPhones,
        parameter_mappings: selectedTemplateParameterNames.map((name) => ({
          name,
          source: templateParameterMappings[name]?.source || "custom",
          fallback: templateParameterMappings[name]?.fallback || "",
        })),
      });
      setTestSendResult(result || null);
    } catch (error) {
      setWhatsappSendError(error?.response?.data?.detail || error?.message || "Failed to send WhatsApp test.");
    } finally {
      setIsWhatsAppTestSending(false);
    }
  }

  async function onSubmitWhatsAppTemplateRequest(event) {
    event?.preventDefault();
    event?.stopPropagation();
    setHasTemplateSubmitAttempted(true);
    if (!onCreateWhatsAppTemplateRequest || !templateRequestValidation.isValid) {
      return;
    }
    setIsSubmittingTemplateRequest(true);
    setWhatsappSendError("");
    try {
      await onCreateWhatsAppTemplateRequest({
        element_name: normalizeTemplateName(templateRequestName),
        category: templateRequestCategory,
        sub_category: "STANDARD",
        language: "en",
        body: templateRequestBody.trim(),
        footer: templateRequestFooter.trim() || null,
        buttons_type: "NONE",
        buttons: [],
        custom_params: templateRequestValidation.samples,
      });
      setTemplateRequestName("");
      setTemplateRequestCategory("MARKETING");
      setTemplateRequestBody("");
      setTemplateRequestFooter("");
      setTemplateRequestSamples("");
      setHasTemplateSubmitAttempted(false);
      setTemplateLibraryFilter("review");
      setIsTemplateRequestOpen(false);
      setCampaignWorkspace("library");
    } catch (error) {
      setWhatsappSendError(error?.response?.data?.detail || error?.message || "Failed to submit WhatsApp template.");
    } finally {
      setIsSubmittingTemplateRequest(false);
    }
  }

  function adjustRecoveryPct(direction) {
    setRecoveryPct((current) => {
      if (direction === "inc") {
        return Math.min(WHAT_IF_MAX, current + WHAT_IF_STEP);
      }
      return Math.max(WHAT_IF_MIN, current - WHAT_IF_STEP);
    });
  }

  return (
    <div className="segment-modal-overlay" onClick={onClose}>
      <div className="segment-modal-card" onClick={(event) => event.stopPropagation()}>
        <div className="segment-modal-head">
          <div>
            <div className="segment-modal-kicker">WhatsApp Campaign Studio</div>
            <div className="segment-modal-title">
              <span>{context.segment} ({formatNumber(context.customerCount)})</span>
              <span className="wati-segment-insight">
                <span>{context.segment} Contribution</span>
                <strong>{formatCurrency(segmentContribution.segmentRevenue)}</strong>
                <small>
                  {segmentContribution.revenueShare ? `${segmentContribution.revenueShare.toFixed(1)}% of revenue` : `${formatNumber(context.customerCount)} customers`}
                  {" - "}
                  {formatCurrency(segmentContribution.averageSpend)} avg/customer
                </small>
              </span>
            </div>
          </div>
          <div className="segment-modal-head-actions">
            <button
              type="button"
              className={`segment-mode-btn ${isTestingMode ? "active" : ""}`.trim()}
              onClick={onToggleTestingMode}
            >
              {isTestingMode ? "Campaign" : "Testing Mode"}
            </button>
            <button type="button" className="action-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <div className="segment-modal-body">
          <div className="segment-action-studio-content">
            {hasWhatsAppStudio ? (
              <div className="wati-studio-panel segment-whatsapp-studio">
                {isTestingMode && testWorkspace === "numbers" ? (
                  <div className="whatsapp-test-panel">
                    <div className="whatsapp-panel-head">
                      <div>
                        <div className="wati-console-title">Testing Mode</div>
                        <div className="wati-console-subtitle">Enter test numbers, choose a template, map parameters, then send a safe test.</div>
                      </div>
                      <span className={`wati-template-status-pill ${selectedWhatsAppTemplate ? "approved" : "paused"}`.trim()}>
                        {selectedWhatsAppTemplate ? "Ready" : "Needs Approved Template"}
                      </span>
                    </div>
                    <div className="whatsapp-test-start">
                      <label className="wati-field">
                        <span>Test Number or Numbers</span>
                        <textarea
                          value={testPhonesText}
                          onChange={(event) => {
                            setTestPhonesText(event.target.value);
                            setTestSendResult(null);
                            setWhatsappSendError("");
                          }}
                          placeholder={"9344546776\n919344546776\n+91 93445 46776"}
                          rows={8}
                        />
                        <small className="wati-field-hint">Add one number per line, or separate numbers with comma/semicolon.</small>
                      </label>
                      <div className="whatsapp-test-summary">
                        <span>Test recipients</span>
                        <strong>{formatNumber(normalizedTestPhones.length)}</strong>
                        <small>{normalizedTestPhones.length ? normalizedTestPhones.join(", ") : "Enter at least one WhatsApp number"}</small>
                        <button
                          type="button"
                          className="segment-primary-btn"
                          onClick={() => {
                            if (normalizedTestPhones.length) {
                              setTestWorkspace("library");
                            }
                          }}
                          disabled={!normalizedTestPhones.length}
                        >
                          Continue to Template Library
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                <div className={`wati-campaign-workspace premium-studio ${isTestingMode ? "test-mode" : ""} ${activeWorkspace === "send" ? "send-mode" : "library-mode"}`.trim()}>
                  <div className="wati-campaign-stage">
                    <div className={`wati-library-layout wati-stage-pane ${activeWorkspace === "library" ? "active" : "inactive"}`.trim()}>
                      <div className="wati-library-top">
                        <div>
                          <div className="wati-console-title wati-library-title">Template Library</div>
                          <div className="wati-console-subtitle">
                            {isTestingMode
                              ? `Choose an approved template for ${formatNumber(normalizedTestPhones.length)} test number(s).`
                              : "Browse every usable template, review status, and select one campaign path."}
                          </div>
                        </div>
                        {isTestingMode ? (
                          <button type="button" className="segment-secondary-btn wati-back-btn" onClick={() => setTestWorkspace("numbers")}>
                            <span className="wati-back-icon" aria-hidden="true" />
                            <span>Edit Numbers</span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="segment-primary-btn"
                            onClick={() => setIsTemplateRequestOpen(true)}
                            disabled={!whatsappConnection?.connected || !onCreateWhatsAppTemplateRequest}
                          >
                            Request Template
                          </button>
                        )}
                      </div>

                      <div className="wati-template-status-strip" aria-label="Template library filters">
                        {templateFilterOptions.map((option) => (
                          <button
                            type="button"
                            key={option.key}
                            className={`wati-template-filter-card ${option.className} ${templateLibraryFilter === option.key ? "active" : ""}`.trim()}
                            onClick={() => {
                              setTemplateLibraryFilter(option.key);
                              setLibraryTemplateName("");
                            }}
                          >
                            <span className="wati-template-filter-label">
                              <i className={`wati-template-filter-icon ${option.className}`} aria-hidden="true" />
                              <span>{option.label}</span>
                            </span>
                            <strong>{formatNumber(option.value)}</strong>
                          </button>
                        ))}
                      </div>
                      {whatsappTemplateLoadError ? (
                        <div className="whatsapp-error-state">{whatsappTemplateLoadError}</div>
                      ) : null}

                      <div className="wati-library-grid">
                        <div className="wati-library-sidebar">
                          {visibleTemplateLibraryGroups.map((group) => (
                            <section key={group.key} className={`wati-template-category ${group.className}`}>
                              <div className="wati-template-category-head">
                                <div className={`wati-template-category-title ${isTemplateSearchOpen || templateLibraryQuery ? "search-open" : ""}`}>
                                  <span>{group.label}</span>
                                  <div className="wati-category-search">
                                    <button
                                      type="button"
                                      className="wati-category-search-btn"
                                      onClick={() => setIsTemplateSearchOpen((value) => !value)}
                                      aria-label="Search templates"
                                      aria-expanded={isTemplateSearchOpen || Boolean(templateLibraryQuery)}
                                    >
                                      <span aria-hidden="true" />
                                    </button>
                                    <input
                                      ref={templateSearchInputRef}
                                      value={templateLibraryQuery}
                                      onChange={(event) => setTemplateLibraryQuery(event.target.value)}
                                      placeholder="Search templates"
                                      aria-label="Search templates"
                                    />
                                    {(isTemplateSearchOpen || templateLibraryQuery) ? (
                                      <button
                                        type="button"
                                        className="wati-category-search-clear"
                                        onClick={() => {
                                          setTemplateLibraryQuery("");
                                          setIsTemplateSearchOpen(false);
                                        }}
                                        aria-label="Close template search"
                                      >
                                        x
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                                <strong>{formatNumber(group.templates.length)}</strong>
                              </div>

                              {group.templates.length ? group.templates.map((template) => {
                                const meta = getWhatsAppStatusMeta(template.status);
                                const isSelected = template.name === previewWhatsAppTemplate?.name;
                                return (
                                  <button
                                    key={`${template.name}:${template.language || "en_US"}`}
                                    type="button"
                                    className={`wati-template-list-item ${isSelected ? "active" : ""}`.trim()}
                                    onClick={() => {
                                      setLibraryTemplateName(template.name);
                                      if (isApprovedWhatsAppTemplate(template)) {
                                        setSelectedTemplateName(template.name);
                                      }
                                    }}
                                  >
                                    <span className="wati-template-list-name">{template.display_name || template.name}</span>
                                    <span className={`wati-template-status-pill ${meta.key}`.trim()}>{meta.label}</span>
                                  </button>
                                );
                              }) : (
                                <div className="wati-template-empty">No templates here yet.</div>
                              )}
                            </section>
                          ))}
                        </div>

                        <div className="wati-template-detail library-preview">
                          {previewWhatsAppTemplate ? (
                            <>
                              <div className="wati-template-detail-head">
                                <div>
                                  <div className="wati-template-name">{previewWhatsAppTemplate.display_name || previewWhatsAppTemplate.name}</div>
                                  <div className="wati-template-meta">
                                    {previewWhatsAppTemplate.category ? <span>{previewWhatsAppTemplate.category}</span> : null}
                                    {previewWhatsAppTemplate.language ? <span>{previewWhatsAppTemplate.language}</span> : null}
                                  </div>
                                </div>
                                <span className={`wati-template-status-pill ${previewWhatsAppStatus.key}`.trim()}>
                                  {previewWhatsAppStatus.label}
                                </span>
                              </div>
                              {previewWhatsAppTemplate.body ? <div className="wati-template-preview">{previewWhatsAppTemplate.body}</div> : null}
                              {!isApprovedWhatsAppTemplate(previewWhatsAppTemplate) ? (
                                <div className="wati-template-warning">
                                  This template is visible for tracking. Campaign sending is available only after approval.
                                </div>
                              ) : null}
                              {isApprovedWhatsAppTemplate(previewWhatsAppTemplate) ? (
                                <button
                                  type="button"
                                  className="segment-primary-btn"
                                  onClick={() => {
                                    setLibraryTemplateName(previewWhatsAppTemplate.name);
                                    setSelectedTemplateName(previewWhatsAppTemplate.name);
                                    setActiveWorkspace("send");
                                  }}
                                >
                                  Select Template
                                </button>
                              ) : null}
                            </>
                          ) : (
                            <div className="wati-template-empty">No template selected.</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={`wati-send-layout wati-stage-pane ${activeWorkspace === "send" ? "active" : "inactive"}`.trim()}>
                      <div className="wati-campaign-brief">
                        <div>
                          <span>{isTestingMode ? "Test setup" : "Campaign setup"}</span>
                          <strong>{selectedWhatsAppTemplate?.display_name || selectedWhatsAppTemplate?.name || "Select an approved template"}</strong>
                          <small>
                            {isTestingMode
                              ? `Sending test to ${formatNumber(normalizedTestPhones.length)} number(s): ${normalizedTestPhones.join(", ")}`
                              : "Map parameters, preview the message, then send to all or choose exact recipients."}
                          </small>
                        </div>
                        <button type="button" className="segment-secondary-btn wati-back-btn" onClick={() => setActiveWorkspace("library")}>
                          <span className="wati-back-icon" aria-hidden="true" />
                          <span>Back to Templates</span>
                        </button>
                      </div>

                      <div className="wati-send-main">
                        <div className="wati-send-step compact-template-step">
                          <div className="wati-send-step-title-row">
                            <div className="wati-send-step-head">
                              <span>1</span>
                              <strong>Choose Template</strong>
                            </div>
                            {selectedWhatsAppTemplate ? <span className="wati-template-status-pill approved">Approved</span> : null}
                          </div>
                          <div className="wati-field wati-send-template-select">
                            <span>Template</span>
                            <div className="wati-template-picker">
                              <button
                                type="button"
                                className="wati-template-picker-trigger"
                                onClick={() => setIsTemplatePickerOpen((value) => !value)}
                                disabled={!approvedWhatsAppTemplates.length}
                              >
                                <span>
                                  <strong>{selectedWhatsAppTemplate?.display_name || selectedWhatsAppTemplate?.name || "No approved templates"}</strong>
                                  <small>{approvedWhatsAppTemplates.length ? `${formatNumber(approvedWhatsAppTemplates.length)} approved templates` : "Request a template to begin"}</small>
                                </span>
                                <b>v</b>
                              </button>
                              {isTemplatePickerOpen ? (
                                <div className="wati-template-picker-menu">
                                  <div className="wati-template-picker-search">
                                    <input
                                      value={templatePickerQuery}
                                      onChange={(event) => setTemplatePickerQuery(event.target.value)}
                                      placeholder="Search approved templates"
                                    />
                                  </div>
                                  {filteredApprovedWhatsAppTemplates.map((template) => (
                                    <button
                                      key={`${template.name}:${template.language || "en_US"}`}
                                      type="button"
                                      className={`wati-template-picker-option ${template.name === selectedTemplateName ? "active" : ""}`.trim()}
                                      onClick={() => {
                                        setSelectedTemplateName(template.name);
                                        setLibraryTemplateName(template.name);
                                        setIsTemplatePickerOpen(false);
                                        setTemplatePickerQuery("");
                                      }}
                                    >
                                      <span>
                                        <strong>{template.display_name || template.name}</strong>
                                        <small>{[template.category, template.language].filter(Boolean).join(" - ")}</small>
                                      </span>
                                      <em>Approved</em>
                                    </button>
                                  ))}
                                  {!filteredApprovedWhatsAppTemplates.length ? <div className="wati-template-picker-empty">No approved templates found.</div> : null}
                                </div>
                              ) : null}
                            </div>
                          </div>
                          {selectedWhatsAppTemplate ? (
                            <div className="wati-template-detail send-preview">
                              <div className="wati-send-preview-toolbar">
                                <div>
                                  <div className="wati-template-name">{selectedWhatsAppTemplate.display_name || selectedWhatsAppTemplate.name}</div>
                                  <div className="wati-template-meta">
                                    {selectedWhatsAppTemplate.category ? <span>{selectedWhatsAppTemplate.category}</span> : null}
                                    {selectedWhatsAppTemplate.language ? <span>{selectedWhatsAppTemplate.language}</span> : null}
                                  </div>
                                </div>
                                <span>Preview</span>
                              </div>
                              <div className="wati-send-phone-preview">
                                <div className="wati-send-preview-screen">
                                  <div className="wati-template-preview">{selectedWhatsAppTemplate.body || "No template body available."}</div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="wati-template-empty">No approved template available for sending.</div>
                          )}
                        </div>

                        <div className="wati-send-step compact-parameter-step">
                          <div className="wati-send-step-title-row">
                            <div className="wati-send-step-head">
                              <span>1</span>
                              <strong>Parameter Map</strong>
                            </div>
                            <small className="wati-param-count">{formatNumber(selectedTemplateParameterNames.length)} fields</small>
                          </div>
                          {selectedTemplateParameterNames.length ? (
                            <div className="wati-smart-param-list">
                              {selectedTemplateParameterNames.map((name) => {
                                const mapping = templateParameterMappings[name] || {};
                                const inferred = Boolean(inferWhatsAppParameterSource(name));
                                return (
                                  <div className="wati-smart-param-row" key={name}>
                                    <div className="wati-smart-param-token">
                                      <strong>{`{{${name}}}`}</strong>
                                      <small>{inferred ? "Auto mapped" : "Choose source"}</small>
                                    </div>
                                    <select
                                      value={mapping.source || ""}
                                      onChange={(event) => {
                                        const source = event.target.value;
                                        setTemplateParameterMappings((current) => ({
                                          ...current,
                                          [name]: {
                                            ...(current[name] || {}),
                                            source,
                                            fallback: defaultWhatsAppFallback(source, organizationName),
                                          },
                                        }));
                                      }}
                                    >
                                      <option value="">Select field</option>
                                      {WHATSAPP_PARAMETER_SOURCES.map((source) => (
                                        <option key={source.key} value={source.key}>{source.label}</option>
                                      ))}
                                    </select>
                                    <input
                                      value={mapping.fallback || ""}
                                      onChange={(event) => {
                                        setTemplateParameterMappings((current) => ({
                                          ...current,
                                          [name]: { ...(current[name] || {}), fallback: event.target.value },
                                        }));
                                      }}
                                      placeholder={mapping.source === "custom" ? "Custom text" : "Fallback if empty"}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="wati-template-empty">This template has no parameters.</div>
                          )}
                          {missingWhatsAppParameterMappings.length ? (
                            <div className="wati-param-hint">Select source for {missingWhatsAppParameterMappings.join(", ")}</div>
                          ) : null}
                        </div>
                      </div>

                      <div className="wati-send-console premium-whatsapp-composer">
                        <div className="wati-live-preview-card">
                          <div className="wati-live-preview-head">
                            <div>
                              <span>{isTestingMode ? "Test preview" : "Live preview"}</span>
                              <strong>{isTestingMode ? normalizedTestPhones[0] || "Test recipient" : previewRecipient ? getCustomerDisplayName(previewRecipient) : "Sample recipient"}</strong>
                            </div>
                            <small>1 of {formatNumber(Math.max(1, activeRecipientCount || 1))}</small>
                          </div>
                          <div className="wati-live-phone">
                            <div className="wati-live-bubble">
                              {previewMessageBody || "Choose an approved template to preview the final message."}
                              <span>10:32</span>
                            </div>
                          </div>
                          <small className="wati-live-note">
                            {isTestingMode ? "This test sends only to the numbers entered before this flow." : "Preview uses mapped values from a real segment customer."}
                          </small>
                          <span className={`wati-template-status-pill ${whatsappConnection?.connected && selectedWhatsAppTemplate ? "approved" : "paused"}`.trim()}>
                            {whatsappConnection?.connected && selectedWhatsAppTemplate ? "Ready" : "Needs Connection"}
                          </span>

                          <div className="campaign-review-cost review-wati-cost whatsapp-cost-hidden">
                            <div>
                              <span>Recipients</span>
                              <strong>{formatNumber(activeRecipientCount)}</strong>
                              <small>{isTestingMode ? "test numbers" : `${formatNumber(segmentCustomers.length)} total in segment`}</small>
                            </div>
                            <div className="whatsapp-hidden-cost-metric">
                              <span>Estimated Meta cost</span>
                              <strong>{formatCurrency(activeEstimatedCost)}</strong>
                              <small>{formatCurrency(whatsappCostRate)} x {formatNumber(activeRecipientCount)} {String(selectedWhatsAppTemplate?.category || "MARKETING").toLowerCase()} messages</small>
                            </div>
                            <div>
                              <span>Delivered</span>
                              <strong>{formatNumber(whatsappAnalytics?.totals?.delivered || 0)}</strong>
                              <small>{formatNumber(whatsappAnalytics?.totals?.failed || 0)} failed logged</small>
                            </div>
                          </div>

                          <div className="wati-send-actions">
                            {isTestingMode ? (
                              <>
                                <button
                                  type="button"
                                  className="segment-primary-btn"
                                  onClick={onConfirmWhatsAppTest}
                                  disabled={isWhatsAppTestSending || !whatsappConnection?.connected || !selectedWhatsAppTemplate || !normalizedTestPhones.length || Boolean(missingWhatsAppParameterMappings.length)}
                                >
                                  {isWhatsAppTestSending ? "Sending Test..." : `Review and Send (${formatNumber(normalizedTestPhones.length)} number${normalizedTestPhones.length === 1 ? "" : "s"})`}
                                </button>
                                <button
                                  type="button"
                                  className="segment-secondary-btn"
                                  onClick={() => setTestWorkspace("numbers")}
                                  disabled={isWhatsAppTestSending}
                                >
                                  Edit Numbers
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="segment-primary-btn"
                                  onClick={(event) => onOpenWhatsAppReview(event, [])}
                                  disabled={!whatsappConnection?.connected || !selectedWhatsAppTemplate || !segmentCustomers.length || Boolean(missingWhatsAppParameterMappings.length)}
                                >
                                  Review and Send ({formatNumber(segmentCustomers.length)} {customerPlural.toLowerCase()})
                                </button>
                                <button
                                  type="button"
                                  className="segment-secondary-btn"
                                  onClick={onOpenWhatsAppRecipientPicker}
                                  disabled={!whatsappConnection?.connected || !selectedWhatsAppTemplate || !segmentCustomers.length || Boolean(missingWhatsAppParameterMappings.length)}
                                >
                                  Choose Recipients
                                </button>
                              </>
                            )}
                          </div>
                          {testSendResult && isTestingMode ? (
                            <div className={testSendResult.failed ? "whatsapp-error-state" : "whatsapp-success-state"}>
                              <strong>
                                Sent {testSendResult.sent || 0}/{testSendResult.recipients || 0}
                                {testSendResult.failed ? ` - Failed: ${testSendResult.failed}` : ""}
                              </strong>
                              {testSendFailureMessages.length ? <small>{testSendFailureMessages[0]}</small> : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {whatsappSendError ? <div className="whatsapp-error-state">{whatsappSendError}</div> : null}
              </div>
            ) : null}

            {!hasWhatsAppStudio ? (
              <>
                <div className="customer-action-title">Suggestive Actions (Segment Card)</div>

            <div
              className={`segment-actions-dropzone ${isDragOver ? "drag-over" : ""}`}
              onDrop={onDropToSegment}
              onDragOver={onDragOverSegment}
              onDragLeave={() => setIsDragOver(false)}
            >
              {displayedSegmentActions.map((action) => (
                (() => {
                  const isSelected = selectedActionType === action.type;
                  const isCustomMessageAction = action.type === CUSTOM_MESSAGE_ACTION_TYPE;
                  return (
                <div
                  key={action.type}
                  className={`segment-action-item ${isSelected ? "active" : ""}`}
                  ref={(node) => {
                    if (node) actionCardRefs.current.set(action.type, node);
                    else actionCardRefs.current.delete(action.type);
                  }}
                  draggable
                  onDragStart={() => onActionDragStart(action.type, "segment")}
                  onClick={() => onSelectAction(action)}
                >
                  <div className="segment-action-item-title-wrap">
                    <div className="segment-action-item-title">{action.title}</div>
                    {isNewAction(action) ? <span className="new-action-badge">NEW</span> : null}
                  </div>
                  <div className="segment-action-item-note">{action.note}</div>

                  <div
                    className={`segment-inline-editor ${isSelected ? "open" : "closed"}`}
                    onClick={(event) => event.stopPropagation()}
                    aria-hidden={!isSelected}
                  >
                    {isSelected && isCustomMessageAction ? (
                      <>
                        <textarea
                          className="segment-custom-message"
                          placeholder="Type your custom segment message..."
                          value={customMessageText}
                          onChange={(event) => setCustomMessageText(event.target.value)}
                          rows={5}
                        />
                        <div className="segment-action-row" style={{ marginTop: 8 }}>
                          <button
                            type="button"
                            className="segment-secondary-btn"
                            onClick={onOpenCustomRecipientPicker}
                            disabled={Boolean(sendingActionType) || !customMessageText.trim() || !segmentCustomers.length}
                          >
                            Choose Recipients
                          </button>
                          <button
                            type="button"
                            className="segment-primary-btn"
                            onClick={onSendCustomMessage}
                            disabled={Boolean(sendingActionType) || !customMessageText.trim()}
                          >
                            {sendingActionType === "custom" ? (
                              <span className="btn-inline-loading">
                                <span className="btn-spinner" />
                                Sending...
                              </span>
                            ) : (
                              "Send"
                            )}
                          </button>
                        </div>
                      </>
                    ) : isSelected ? (
                      <>
                        <textarea
                          className="segment-custom-message"
                          placeholder="Edit message for this action"
                          value={draftMessage}
                          onChange={(event) => setDraftMessage(event.target.value)}
                          rows={5}
                        />
                        <div className="segment-action-row" style={{ marginTop: 8 }}>
                          <button
                            type="button"
                            className="segment-secondary-btn"
                            onClick={(event) => onResetClick(action, event)}
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            className="segment-secondary-btn"
                            onClick={(event) => onOpenRecipientPicker(action, event)}
                            disabled={Boolean(sendingActionType) || !segmentCustomers.length}
                          >
                            Choose Recipients
                          </button>
                          <button
                            type="button"
                            className="segment-primary-btn"
                            onClick={(event) => onSaveAndSendClick(action, event)}
                            disabled={Boolean(sendingActionType) || !isActionVisible(action)}
                          >
                            {sendingActionType === `${action.type}::save-send` ? (
                              <span className="btn-inline-loading">
                                <span className="btn-spinner" />
                                Saving & Sending...
                              </span>
                            ) : (
                              "Save and Send"
                            )}
                          </button>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
                  );
                })()
              ))}
              {!segmentActions.length ? (
                <div className="segment-empty">Drop actions here from the pool below.</div>
              ) : null}
            </div>

            <div className="segment-pool-head" style={{ marginTop: 10 }}>
              <div className="customer-action-title">Suggestive Action Pool (All Actions)</div>
              <div className="segment-pool-controls">
                <button
                  type="button"
                  className="segment-secondary-btn"
                  onClick={() =>
                    setIsPoolOpen((current) => {
                      return !current;
                    })
                  }
                >
                  {isPoolOpen ? "Close Pool" : "Open Pool"}
                </button>
              </div>
            </div>

            {isPoolOpen ? (
              <div
                className={`action-pool-list ${isPoolDragOver ? "drag-over" : ""}`}
                onDrop={onDropToPool}
                onDragOver={onDragOverPool}
                onDragLeave={() => setIsPoolDragOver(false)}
              >
                {poolActions.map((action) => (
                  <div
                    key={action.type}
                    className="segment-action-item pool"
                    draggable
                    onDragStart={() => onActionDragStart(action.type, "pool")}
                    onClick={() => onSelectAction(action)}
                  >
                    <div className="segment-action-item-title-wrap">
                      <div className="segment-action-item-title">{action.title}</div>
                      {isNewAction(action) ? <span className="new-action-badge">NEW</span> : null}
                    </div>
                    <div className="segment-action-item-note">{action.note}</div>
                    <div className={`segment-status-pill ${isInSegment(action) ? "active" : "inactive"}`}>
                      {isInSegment(action) ? "Added to segment" : "Only in pool"}
                    </div>

                    {editingActionType === action.type ? (
                      <div className="suggestive-create-card" onClick={(event) => event.stopPropagation()}>
                        <input
                          className="suggestive-input"
                          placeholder="Action title"
                          value={editTitle}
                          onChange={(event) => setEditTitle(event.target.value)}
                        />
                        <input
                          className="suggestive-input"
                          placeholder="Short note"
                          value={editNote}
                          onChange={(event) => setEditNote(event.target.value)}
                        />
                        <textarea
                          className="segment-custom-message"
                          placeholder="Default message template"
                          value={editMessage}
                          onChange={(event) => setEditMessage(event.target.value)}
                          rows={4}
                        />
                        <div className="segment-action-row" style={{ marginTop: 8 }}>
                          <button
                            type="button"
                            className="segment-secondary-btn"
                            onClick={onCancelEditAction}
                            disabled={isUpdatingAction}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="segment-primary-btn"
                            onClick={onSaveEditAction}
                            disabled={isUpdatingAction || !editTitle.trim()}
                          >
                            {isUpdatingAction ? "Saving..." : "Save Edit"}
                          </button>
                        </div>
                      </div>
                    ) : null}

                    <div className="segment-action-row">
                      <button
                        type="button"
                        className="segment-secondary-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          onToggleSuggestiveAction?.(
                            context.segment,
                            action.type,
                            isInSegment(action)
                          );
                        }}
                      >
                        {isInSegment(action) ? "Remove" : "Add"}
                      </button>
                      <button
                        type="button"
                        className="segment-secondary-btn"
                        onClick={(event) => onBeginEditAction(action, event)}
                        disabled={!action.isCustom}
                        title={action.isCustom ? "Edit custom action" : "Only custom actions can be edited"}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="segment-danger-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          onDeleteSuggestiveAction?.(context.segment, action.type);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
                {!poolActions.length ? <div className="segment-empty">No actions available in pool.</div> : null}
              </div>
            ) : null}

            {isWhatIfEligible ? (
              <>
                <div className="customer-action-title">What-if Recovery</div>
                <div className="whatif-card">
                  <div className="whatif-caption">
                    If you recover <span className="mono-text">{recoveryPct}%</span> of this segment:
                  </div>
                  <div className="whatif-stepper">
                    <button
                      type="button"
                      className="segment-secondary-btn"
                      onClick={() => adjustRecoveryPct("dec")}
                      disabled={recoveryPct <= WHAT_IF_MIN}
                    >
                      -
                    </button>
                    <div className="whatif-stepper-value mono-text">{recoveryPct}%</div>
                    <button
                      type="button"
                      className="segment-secondary-btn"
                      onClick={() => adjustRecoveryPct("inc")}
                      disabled={recoveryPct >= WHAT_IF_MAX}
                    >
                      +
                    </button>
                  </div>
                  <div className="whatif-output">
                    Potential revenue: <strong>{formatCompactCurrency(whatIf.potentialRevenue)}</strong>
                  </div>
                  <div className="whatif-sub">
                    Estimated recovered customers: {formatNumber(whatIf.recoveredCustomers)}
                  </div>
                </div>
              </>
            ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>

      {confirmAction ? (
        <div className="segment-confirm-overlay" onClick={() => setConfirmAction(null)}>
          <div className="segment-confirm-card" onClick={(event) => event.stopPropagation()}>
            <div className="segment-confirm-title">Confirm Send</div>
            <div className="segment-confirm-text">
              This message will be sent to all customers in <strong>{context.segment}</strong> segment.
            </div>
            <div className="segment-confirm-text">
              Estimated recipients: <strong>{formatNumber(context.customerCount || 0)}</strong>
            </div>
            <div className="segment-action-row" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="segment-secondary-btn"
                onClick={() => setConfirmAction(null)}
                disabled={Boolean(sendingActionType)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="segment-primary-btn"
                onClick={onConfirmSaveAndSend}
                disabled={Boolean(sendingActionType)}
              >
                {sendingActionType === `${confirmAction.type}::save-send` ? "Sending..." : "Confirm Send"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isWhatsAppReviewOpen ? (
        <div className="segment-confirm-overlay" onClick={() => setIsWhatsAppReviewOpen(false)}>
          <div className="segment-confirm-card campaign-review-card" onClick={(event) => event.stopPropagation()}>
            <div className="segment-confirm-title">Review WhatsApp Campaign</div>
            <div className="segment-confirm-text">
              Send <strong>{selectedWhatsAppTemplate?.display_name || selectedWhatsAppTemplate?.name}</strong> to <strong>{formatNumber(whatsappSelectedCount)}</strong> customers in <strong>{context.segment}</strong>.
            </div>
            <div className="wati-live-phone campaign-review-phone">
              <div className="wati-live-bubble">
                {previewMessageBody || selectedWhatsAppTemplate?.body || "No preview available."}
                <span>10:32</span>
              </div>
            </div>
            <div className="campaign-review-cost review-wati-cost compact whatsapp-cost-hidden">
              <div>
                <span>Recipients</span>
                <strong>{formatNumber(whatsappSelectedCount)}</strong>
              </div>
              <div className="whatsapp-hidden-cost-metric">
                <span>Estimated Meta cost</span>
                <strong>{formatCurrency(whatsappEstimatedCost)}</strong>
              </div>
            </div>
            {whatsappSendError ? <div className="whatsapp-error-state">{whatsappSendError}</div> : null}
            <div className="segment-action-row" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="segment-secondary-btn"
                onClick={() => setIsWhatsAppReviewOpen(false)}
                disabled={isWhatsAppSending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="segment-primary-btn"
                onClick={onConfirmWhatsAppCampaign}
                disabled={isWhatsAppSending || !whatsappConnection?.connected || !selectedWhatsAppTemplate}
              >
                {isWhatsAppSending ? "Sending..." : "Confirm Send"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isWhatsAppRecipientPickerOpen ? (
        <div
          className="segment-confirm-overlay"
          onClick={() => {
            setIsWhatsAppRecipientPickerOpen(false);
            setWhatsappSelectedRecipientIds([]);
          }}
        >
          <div className="segment-recipient-card whatsapp-recipient-card" onClick={(event) => event.stopPropagation()}>
            <div className="segment-recipient-head-row">
              <div>
                <div className="segment-confirm-title">Target WhatsApp Recipients</div>
                <div className="segment-confirm-text">
                  Select recipients in <strong>{context.segment}</strong> for this approved template.
                </div>
              </div>
              <div className="segment-recipient-search-row">
                <input
                  type="text"
                  className="search-input segment-recipient-search-input"
                  placeholder={`Search ${String(terminology.customer_label || "customer").toLowerCase()} name or phone`}
                  value={whatsappRecipientSearchText}
                  onChange={(event) => setWhatsappRecipientSearchText(event.target.value)}
                />
              </div>
            </div>

            <div className="segment-recipient-toolbar">
              <button
                type="button"
                className="segment-secondary-btn"
                onClick={() => setWhatsappSelectedRecipientIds(filteredWhatsAppRecipients.map((item) => item.customer_id))}
              >
                Select Visible
              </button>
              <button
                type="button"
                className="segment-secondary-btn"
                onClick={() => setWhatsappSelectedRecipientIds([])}
              >
                Clear All
              </button>
              <div className="segment-recipient-count">
                Selected: <strong>{formatNumber(whatsappSelectedRecipientIds.length)}</strong>
              </div>
            </div>

            <div className="segment-recipient-table-wrap">
              <table className="segment-recipient-table">
                <thead>
                  <tr>
                    <th style={{ width: 56 }}>Pick</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>{totalSpendLabel}</th>
                    <th>{activityCountLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWhatsAppRecipients.map((customerItem) => {
                    const checked = whatsappSelectedRecipientIds.includes(customerItem.customer_id);
                    return (
                      <tr key={customerItem.customer_id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => onToggleWhatsAppRecipient(customerItem.customer_id)}
                          />
                        </td>
                        <td>{toTitleCase(customerItem.full_name || customerItem.first_name || "-")}</td>
                        <td className="mono-text">{customerItem.phone_number || "-"}</td>
                        <td className="mono-text">{formatCurrency(customerItem.total_spend)}</td>
                        <td className="mono-text">{formatNumber(customerItem.visit_count)}</td>
                      </tr>
                    );
                  })}
                  {!filteredWhatsAppRecipients.length ? (
                    <tr>
                      <td colSpan={5} className="segment-empty" style={{ textAlign: "center" }}>
                        No {customerPlural.toLowerCase()} match your search.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="segment-action-row" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="segment-secondary-btn"
                onClick={() => {
                  setIsWhatsAppRecipientPickerOpen(false);
                  setWhatsappSelectedRecipientIds([]);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="segment-primary-btn"
                onClick={(event) => {
                  setIsWhatsAppRecipientPickerOpen(false);
                  onOpenWhatsAppReview(event, whatsappSelectedRecipientIds);
                }}
                disabled={!whatsappSelectedRecipientIds.length}
              >
                Review Selected
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isTemplateRequestOpen ? (
        <div
          className="segment-confirm-overlay"
          onClick={() => {
            setHasTemplateSubmitAttempted(false);
            setIsTemplateRequestOpen(false);
          }}
        >
          <div className="wati-template-request-modal" onClick={(event) => event.stopPropagation()}>
            <div className="wati-template-request-head">
              <div>
                <div className="segment-modal-kicker">Template Request</div>
                <div className="segment-confirm-title">Submit WhatsApp Template</div>
              </div>
              <button
                type="button"
                className="action-btn"
                onClick={() => {
                  setHasTemplateSubmitAttempted(false);
                  setIsTemplateRequestOpen(false);
                }}
              >
                Close
              </button>
            </div>
            <div className="wati-template-request-grid">
              <div className="wati-template-request-form">
                <label className="wati-field">
                  <span>Template Name</span>
                  <input
                    className="suggestive-input"
                    placeholder="winback_coupon"
                    value={templateRequestName}
                    onChange={(event) => setTemplateRequestName(sanitizeTemplateNameInput(event.target.value))}
                    onBlur={() => setTemplateRequestName((value) => normalizeTemplateName(value))}
                    maxLength={512}
                  />
                  <small className="wati-field-hint">
                    Spaces become underscores. Safe name: <strong>{templateRequestValidation.normalizedName || "template_name"}</strong>
                  </small>
                  {hasTemplateSubmitAttempted && templateRequestIssuesByField.name.map((issue) => (
                    <small key={`${issue.title}-${issue.detail}`} className={`wati-field-error ${issue.level}`}>{issue.title} {issue.detail}</small>
                  ))}
                </label>
                <label className="wati-field">
                  <span>Message Body</span>
                  <textarea
                    className="segment-custom-message"
                    placeholder={"Hi {{1}}, your offer from {{2}} is ready."}
                    value={templateRequestBody}
                    onChange={(event) => setTemplateRequestBody(event.target.value)}
                    rows={4}
                  />
                  <small className={`wati-field-hint ${templateRequestBody.length > 1024 ? "danger" : ""}`}>
                    {templateRequestBody.length}/1024 characters. Variables must look like {"{{1}}"} or {"{{customer_name}}"}.
                  </small>
                  {hasTemplateSubmitAttempted && templateRequestIssuesByField.body.map((issue) => (
                    <small key={`${issue.title}-${issue.detail}`} className={`wati-field-error ${issue.level}`}>{issue.title} {issue.detail}</small>
                  ))}
                </label>
                <label className="wati-field">
                  <span>Footer</span>
                  <input
                    className="suggestive-input"
                    placeholder="Optional footer"
                    value={templateRequestFooter}
                    onChange={(event) => setTemplateRequestFooter(event.target.value)}
                    maxLength={80}
                  />
                  <small className={`wati-field-hint ${templateRequestFooter.length > 60 ? "danger" : ""}`}>
                    {templateRequestFooter.length}/60 recommended characters.
                  </small>
                  {hasTemplateSubmitAttempted && templateRequestIssuesByField.footer.map((issue) => (
                    <small key={`${issue.title}-${issue.detail}`} className={`wati-field-error ${issue.level}`}>{issue.title} {issue.detail}</small>
                  ))}
                </label>
                <label className="wati-field">
                  <span>Sample Parameter Values</span>
                  <textarea
                    className="segment-custom-message"
                    placeholder={"1=Customer Name\n2=Korv CRM"}
                    value={templateRequestSamples}
                    onChange={(event) => setTemplateRequestSamples(event.target.value)}
                    rows={2}
                  />
                  {hasTemplateSubmitAttempted && templateRequestIssuesByField.samples.map((issue) => (
                    <small key={`${issue.title}-${issue.detail}`} className={`wati-field-error ${issue.level}`}>{issue.title} {issue.detail}</small>
                  ))}
                </label>
              </div>
              <div className="wati-template-request-preview">
                <div className="wati-send-preview-toolbar">
                  <div>
                    <div className="wati-template-name">{templateRequestName.trim() || "new_template"}</div>
                    <div className="wati-template-meta">
                      <span>English</span>
                    </div>
                  </div>
                  <span className="wati-template-status-pill review">In Review</span>
                </div>
                <div className="wati-send-preview-screen">
                  <div className="wati-template-preview">
                    {templateRequestBody || "Your WhatsApp message preview appears here."}
                  </div>
                </div>
                <div className="wati-template-warning">
                  After submit, this request is tracked under In Review until provider approval is complete.
                </div>
                <div className="wati-template-docs">
                  <span>Review checklist</span>
                  <ol>
                    <li><strong>Name:</strong> lowercase, descriptive, and unique.</li>
                    <li><strong>Body:</strong> no adjacent variables, no variable at start or end.</li>
                    <li><strong>Samples:</strong> add one realistic value for each placeholder.</li>
                  </ol>
                </div>
              </div>
            </div>
            {whatsappSendError ? <div className="whatsapp-error-state">{whatsappSendError}</div> : null}
            <div className="wati-template-request-actions">
              <button
                type="button"
                className="segment-secondary-btn"
                onClick={() => {
                  setHasTemplateSubmitAttempted(false);
                  setIsTemplateRequestOpen(false);
                }}
                disabled={isSubmittingTemplateRequest}
              >
                Cancel
              </button>
              <button
                type="button"
                className="segment-primary-btn"
                onClick={onSubmitWhatsAppTemplateRequest}
                disabled={isSubmittingTemplateRequest}
              >
                {isSubmittingTemplateRequest ? "Submitting..." : "Submit For Review"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isRecipientPickerOpen ? (
        <div
          className="segment-confirm-overlay"
          onClick={() => {
            setIsRecipientPickerOpen(false);
            setRecipientPickerAction(null);
            setIsCustomRecipientFlow(false);
            setSelectedRecipientIds([]);
          }}
        >
          <div className="segment-recipient-card" onClick={(event) => event.stopPropagation()}>
            <div className="segment-recipient-head-row">
              <div>
                <div className="segment-confirm-title">
                  {isCustomRecipientFlow ? "Targeted Custom Message" : "Targeted Send"}
                </div>
                <div className="segment-confirm-text">
                  Select recipients in <strong>{context.segment}</strong> segment.
                </div>
              </div>

              <div className="segment-recipient-control-row">
                <div className="segment-recipient-bottom-row">
                  <div className="compact-filter-group">
                    <label className="compact-filter-label"><FilterMetricIcon type="visits" /><span>{activityCountLabel}</span></label>
                    <MetricRangeSlider
                      minValue={recipientMinVisits}
                      maxValue={recipientMaxVisits}
                      rangeMin={recipientVisitRange.min}
                      rangeMax={recipientVisitRange.max}
                      step={1}
                      onMinChange={handleRecipientVisitsInput}
                      onMaxChange={handleRecipientVisitsMaxInput}
                      formatValue={formatNumber}
                    />
                    <div className="compact-filter-inputs">
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        step="1"
                        className="compact-filter-input"
                        placeholder="Min"
                        value={recipientMinVisits}
                        onChange={(e) => handleRecipientVisitsInput(e.target.value)}
                      />
                      <span className="compact-filter-separator">-</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        step="1"
                        className="compact-filter-input"
                        placeholder="Max"
                        value={recipientMaxVisits}
                        onChange={(e) => handleRecipientVisitsMaxInput(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="compact-filter-group">
                    <label className="compact-filter-label"><FilterMetricIcon type="monetary" /><span>{spendLabel}</span></label>
                    <MetricRangeSlider
                      minValue={recipientMinMonetary}
                      maxValue={recipientMaxMonetary}
                      rangeMin={recipientMonetaryRange.min}
                      rangeMax={recipientMonetaryRange.max}
                      step={1}
                      onMinChange={handleRecipientMonetaryInput}
                      onMaxChange={handleRecipientMonetaryMaxInput}
                      formatValue={formatCompactCurrency}
                    />
                    <div className="compact-filter-inputs">
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="1"
                        className="compact-filter-input"
                        placeholder="Min"
                        value={recipientMinMonetary}
                        onChange={(e) => handleRecipientMonetaryInput(e.target.value)}
                      />
                      <span className="compact-filter-separator">-</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="1"
                        className="compact-filter-input"
                        placeholder="Max"
                        value={recipientMaxMonetary}
                        onChange={(e) => handleRecipientMonetaryMaxInput(e.target.value)}
                      />
                    </div>
                  </div>

                  {false && recipientHasActiveFilters && (
                    <button
                      type="button"
                      className="compact-filter-reset"
                      onClick={resetRecipientFilters}
                      title="Clear filters"
                    >
                      x
                    </button>
                  )}
                </div>

                <div className="segment-recipient-search-row">
                  <input
                    type="text"
                    className="search-input segment-recipient-search-input"
                    placeholder={`Search by ${String(terminology.customer_label || "customer").toLowerCase()} name or phone...`}
                    value={recipientSearchText}
                    onChange={(event) => setRecipientSearchText(event.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="segment-recipient-toolbar">
              <button
                type="button"
                className="segment-secondary-btn"
                onClick={() => setSelectedRecipientIds(filteredSegmentCustomers.map((item) => item.customer_id))}
              >
                Select All
              </button>
              <button
                type="button"
                className="segment-secondary-btn"
                onClick={() => setSelectedRecipientIds([])}
              >
                Clear All
              </button>
              <div className="segment-recipient-count">
                Selected: <strong>{formatNumber(selectedRecipientIds.length)}</strong>
              </div>
            </div>

            <div className="segment-recipient-table-wrap">
              <table className="segment-recipient-table">
                <thead>
                  <tr>
                    <th style={{ width: 56 }}>Pick</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>{totalSpendLabel}</th>
                    <th>{activityCountLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSegmentCustomers.map((customerItem) => {
                    const checked = selectedRecipientIds.includes(customerItem.customer_id);
                    return (
                      <tr key={customerItem.customer_id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => onToggleRecipient(customerItem.customer_id)}
                          />
                        </td>
                        <td>{toTitleCase(customerItem.full_name || customerItem.first_name || "-")}</td>
                        <td className="mono-text">{customerItem.phone_number || "-"}</td>
                        <td className="mono-text">{formatCurrency(customerItem.total_spend)}</td>
                        <td className="mono-text">{formatNumber(customerItem.visit_count)}</td>
                      </tr>
                    );
                  })}
                  {!filteredSegmentCustomers.length ? (
                    <tr>
                      <td colSpan={5} className="segment-empty" style={{ textAlign: "center" }}>
                        No {customerPlural.toLowerCase()} match your search.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="segment-action-row" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="segment-secondary-btn"
                onClick={() => {
                  setIsRecipientPickerOpen(false);
                  setRecipientPickerAction(null);
                  setIsCustomRecipientFlow(false);
                  setSelectedRecipientIds([]);
                }}
                disabled={Boolean(sendingActionType)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="segment-primary-btn"
                onClick={onConfirmTargetedSend}
                disabled={Boolean(sendingActionType) || selectedRecipientIds.length === 0}
              >
                {sendingActionType === `${recipientPickerAction?.type}::targeted-send`
                  ? "Sending..."
                  : isCustomRecipientFlow
                  ? "Send Custom Message to Selected"
                  : "Send to Selected"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
